// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AccessDenied } from "@/components/shared/access-denied";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StoreEditTab, type StoreEditTabHandle } from "@/features/stores/store-edit-tab";
import { StoreHardwareTab, type StoreHardwareTabHandle } from "@/features/stores/store-hardware-tab";
import { StoreOperationsTab, type StoreOperationsTabHandle } from "@/features/stores/store-operations-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

const EDIT_TABS = ["general", "operations", "hardware"] as const;

export const Route = createFileRoute("/_authenticated/stores/$storeName/edit")({
  component: StoreEditPage,
});

function StoreEditPage() {
  const { storeName } = Route.useParams();
  const { t: tab } = Route.useSearch();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("back-office");

  const canEdit = hasPermission(PAGE_PERMISSIONS.storesEdit);
  const canViewOperations = hasPermission(PAGE_PERMISSIONS.storesOperations);
  const canViewHardware = hasPermission(PAGE_PERMISSIONS.storesHardware);
  const tabAllowed: Record<string, boolean> = {
    general: canEdit,
    operations: canViewOperations,
    hardware: canViewHardware,
  };

  const [isEditDirty, setIsEditDirty] = useState(false);
  const [isOperationsDirty, setIsOperationsDirty] = useState(false);
  const [isHardwareDirty, setIsHardwareDirty] = useState(false);
  const isFormDirty = isEditDirty || isOperationsDirty || isHardwareDirty;
  const editTabRef = useRef<StoreEditTabHandle>(null);
  const operationsTabRef = useRef<StoreOperationsTabHandle>(null);
  const hardwareTabRef = useRef<StoreHardwareTabHandle>(null);

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    enableBeforeUnload: () => isFormDirty,
    withResolver: true,
  });

  function handleTabChange(tab: string) {
    navigate({
      to: "/stores/$storeName/edit",
      params: { storeName },
      search: tab === "general" ? {} : { t: tab },
      replace: true,
    });
  }

  // The three tabs are permissioned separately, so the page falls back to the first one the user
  // may see — and is denied outright only when none of them are granted.
  const firstAllowedTab = EDIT_TABS.find((name) => tabAllowed[name]);
  if (!firstAllowedTab) return <AccessDenied permission={PAGE_PERMISSIONS.storesEdit} />;
  const activeTab = tab && tabAllowed[tab] ? tab : firstAllowedTab;

  return (
    <div>
      <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
        <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
          <TabsList>
            <TabsIndicator />
            {canEdit && <TabsTrigger value="general">{t("tabs.general")}</TabsTrigger>}
            {canViewOperations && <TabsTrigger value="operations">{t("tabs.operations")}</TabsTrigger>}
            {canViewHardware && <TabsTrigger value="hardware">{t("tabs.hardware")}</TabsTrigger>}
          </TabsList>
        </div>
        {canEdit && (
          <TabsContent value="general" className="space-y-4">
            <StoreEditTab ref={editTabRef} storeName={storeName} onDirtyChange={setIsEditDirty} />
          </TabsContent>
        )}
        {canViewOperations && (
          <TabsContent value="operations" className="space-y-4">
            <StoreOperationsTab ref={operationsTabRef} storeName={storeName} onDirtyChange={setIsOperationsDirty} />
          </TabsContent>
        )}
        {canViewHardware && (
          <TabsContent value="hardware" className="space-y-4">
            <StoreHardwareTab ref={hardwareTabRef} storeName={storeName} onDirtyChange={setIsHardwareDirty} />
          </TabsContent>
        )}
      </Tabs>

      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          editTabRef.current?.reset();
          operationsTabRef.current?.reset();
          hardwareTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
